import { getProductForDataset, type ProductCode } from './dataset-utils.ts';

/** Every partner gets these, so they never decide whether something is shown. */
const ALWAYS_SHOWN_PRODUCTS: ReadonlySet<ProductCode> = new Set(['Core', 'STRATEGYFINDER']);

/**
 * The products a dashboard draws on, read from the datasets its widgets query.
 * Datasets resolve through the registered catalog, so this is only meaningful
 * once the host route has registered it; an unknown dataset reads as Core, so a
 * failed catalog leaves every dashboard listed rather than hiding them all.
 *
 * @param searchConfig the dashboard's saved search config; its `widgets` list carries one `dataset` slug per widget
 * @returns distinct product codes, empty for a dashboard with no widgets
 */
export function dashboardProducts(searchConfig: unknown): ProductCode[] {
  const widgets = (searchConfig as { widgets?: unknown } | null | undefined)?.widgets;
  if (!Array.isArray(widgets)) return [];

  const products = new Set<ProductCode>();
  for (const widget of widgets) {
    const dataset = (widget as { dataset?: unknown } | null)?.dataset;
    if (typeof dataset === 'string' && dataset) products.add(getProductForDataset(dataset));
  }
  return [...products];
}

/**
 * Whether a report or dashboard drawing on `products` belongs on a partner's
 * listing. Core and Strategy Finder are neutral: something built only from
 * them is always shown, and they never make a licensed-product item visible on
 * their own. Otherwise ANY one licensed product the company has is enough.
 *
 * @param products what the item draws on — a report's single product, or a dashboard's widget products
 * @param enabledProducts the company's licensed products; undefined applies no filter
 */
export function isVisibleForCompanyProducts(
  products: ProductCode[],
  enabledProducts: ProductCode[] | undefined
): boolean {
  if (enabledProducts === undefined) return true;
  const licensed = products.filter((product) => !ALWAYS_SHOWN_PRODUCTS.has(product));
  return licensed.length === 0 || licensed.some((product) => enabledProducts.includes(product));
}
